import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../../components/Navbar';
import { ArrowRight, Upload, X } from 'lucide-react';
import { CATEGORIES, getSubcategories } from '../../data/categories';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAIN_CATEGORIES = Object.keys(CATEGORIES);

const ProviderOnboarding = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    categories: [],
    subcategories: [],
    location_city: '',
    location_zip: '',
    location_state: '',
    short_description: '',
    long_description: '',
    price_range: '',
    base_price_numeric: null,
    base_price_from: '',
    instagram_link: '',
    website_link: '',
    email: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Pre-fill form if data exists
      if (response.data.company_name) {
        setFormData({
          company_name: response.data.company_name || '',
          categories: response.data.categories || [],
          subcategories: response.data.subcategories || [],
          location_city: response.data.location_city || '',
          location_zip: response.data.location_zip || '',
          location_state: response.data.location_state || '',
          short_description: response.data.short_description || '',
          long_description: response.data.long_description || '',
          price_range: response.data.price_range || '',
          base_price_numeric: response.data.base_price_numeric || null,
          base_price_from: response.data.base_price_from || '',
          instagram_link: response.data.instagram_link || '',
          website_link: response.data.website_link || '',
          email: response.data.email || user?.email || ''
        });
      } else {
        // Initialize email with user's email on first load
        setFormData(prev => ({
          ...prev,
          email: user?.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    }
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSubcategoryToggle = (subcategory) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.includes(subcategory)
        ? prev.subcategories.filter(s => s !== subcategory)
        : [...prev.subcategories, subcategory]
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;
      
      let storageError = null;

      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          await axios.post(`${API}/provider/upload-image`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000 // 30 seconds timeout
          });
          successCount++;
        } catch (err) {
          console.error(`Upload error for ${file.name}:`, err);
          errorCount++;

          // Check for structured error response
          const detail = err.response?.data?.detail;
          if (detail && typeof detail === 'object') {
            console.error('Upload error details:', {
              code: detail.code,
              technical: detail.technical,
              storageInitError: detail.storage_init_error
            });
            // Store first storage error for display
            if (detail.code === 'STORAGE_NOT_CONFIGURED' && !storageError) {
              storageError = detail;
            }
          }
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} Bild(er) hochgeladen!`);
        await loadProfile();
      }

      if (errorCount > 0) {
        // Show more specific error message if storage is the issue
        if (storageError) {
          toast.error('Speicher nicht verfügbar. Bitte versuche es später erneut oder kontaktiere den Support.');
        } else {
          toast.error(`${errorCount} Bild(er) konnten nicht hochgeladen werden`);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail;

      // Handle structured error response
      if (detail && typeof detail === 'object') {
        console.error('Upload error details:', {
          code: detail.code,
          technical: detail.technical,
          storageInitError: detail.storage_init_error
        });

        if (detail.code === 'STORAGE_NOT_CONFIGURED') {
          toast.error('Speicher nicht verfügbar. Bitte versuche es später erneut oder kontaktiere den Support.');
        } else {
          toast.error(detail.message || 'Fehler beim Hochladen. Bitte versuche es erneut.');
        }
      } else {
        toast.error('Fehler beim Hochladen. Bitte versuche es erneut.');
      }
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/delete-image`, 
        { url: imageUrl },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Bild gelöscht');
      await loadProfile();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!formData.company_name || formData.categories.length === 0) {
        toast.error('Bitte fülle alle Pflichtfelder aus');
        return;
      }
    } else if (step === 2) {
      if (!formData.location_city || !formData.location_zip) {
        toast.error('Bitte gib deinen Standort an');
        return;
      }
    } else if (step === 3) {
      if (!formData.short_description) {
        toast.error('Bitte füge eine Kurzbeschreibung hinzu');
        return;
      }
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/provider/profile`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (step < 4) {
        setStep(step + 1);
        toast.success('Gespeichert ✓');
      } else {
        // Mark onboarding as complete
        if (user?.id) {
          localStorage.setItem(`onboarding_seen_${user.id}`, 'true');
        }
        toast.success('Profil erfolgreich erstellt!');
        navigate('/provider/dashboard');
      }
    } catch (error) {
      console.error('Update error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0D1]/30 via-white to-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`flex-1 h-2 rounded-full mx-1 ${
                    s <= step ? 'bg-[#2B2142]' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-gray-600">Schritt {step} von 4</p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {step === 1 && (
              <div data-testid="onboarding-step-1">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Grunddaten</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Firmenname / Dein Name *
                    </label>
                    <input
                      type="text"
                      data-testid="company-name-input"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="z.B. Event-Fotografie Schmidt"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Kontakt-E-Mail *
                    </label>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="kontakt@beispiel.de"
                    />
                    <p className="text-sm text-gray-500 mt-1">Diese E-Mail wird für Anfragen von Kunden verwendet</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hauptkategorien * (mind. 1 auswählen)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {MAIN_CATEGORIES.map((category) => (
                        <button
                          key={category}
                          type="button"
                          data-testid={`category-${category}`}
                          onClick={() => handleCategoryToggle(category)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            formData.categories.includes(category)
                              ? 'border-[#2B2142] bg-[#FFF0D1]/30 text-[#2B2142]'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="font-semibold">{category}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {formData.categories.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Unterkategorien (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {formData.categories.flatMap(cat => getSubcategories(cat)).map((sub) => (
                          <button
                            key={sub}
                            type="button"
                            data-testid={`subcategory-${sub}`}
                            onClick={() => handleSubcategoryToggle(sub)}
                            className={`px-4 py-2 rounded-full border-2 text-sm transition-all ${
                              formData.subcategories.includes(sub)
                                ? 'border-[#2B2142] bg-[#FFF0D1]/30 text-[#2B2142]'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {sub}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {step === 2 && (
              <div data-testid="onboarding-step-2">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Standort</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        PLZ *
                      </label>
                      <input
                        type="text"
                        data-testid="zip-input"
                        value={formData.location_zip}
                        onChange={(e) => setFormData({ ...formData, location_zip: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                        placeholder="70173"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Stadt *
                      </label>
                      <input
                        type="text"
                        data-testid="city-input"
                        value={formData.location_city}
                        onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                        placeholder="Stuttgart"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bundesland
                    </label>
                    <select
                      data-testid="state-input"
                      value={formData.location_state}
                      onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Baden-Württemberg">Baden-Württemberg</option>
                      <option value="Bayern">Bayern</option>
                      <option value="Berlin">Berlin</option>
                      <option value="Brandenburg">Brandenburg</option>
                      <option value="Bremen">Bremen</option>
                      <option value="Hamburg">Hamburg</option>
                      <option value="Hessen">Hessen</option>
                      <option value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</option>
                      <option value="Niedersachsen">Niedersachsen</option>
                      <option value="Nordrhein-Westfalen">Nordrhein-Westfalen</option>
                      <option value="Rheinland-Pfalz">Rheinland-Pfalz</option>
                      <option value="Saarland">Saarland</option>
                      <option value="Sachsen">Sachsen</option>
                      <option value="Sachsen-Anhalt">Sachsen-Anhalt</option>
                      <option value="Schleswig-Holstein">Schleswig-Holstein</option>
                      <option value="Thüringen">Thüringen</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
            
            {step === 3 && (
              <div data-testid="onboarding-step-3">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Beschreibung & Preise</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Kurzbeschreibung *
                    </label>
                    <textarea
                      data-testid="short-desc-input"
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      rows={3}
                      maxLength={150}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="Eine kurze Zusammenfassung deiner Dienstleistung (max. 150 Zeichen)"
                    />
                    <p className="text-sm text-gray-500 mt-1">{formData.short_description.length}/150 Zeichen</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Langbeschreibung
                    </label>
                    <textarea
                      data-testid="long-desc-input"
                      value={formData.long_description}
                      onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="Erzähle mehr über deine Services, Erfahrung und was dich besonders macht..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Basis-Preis (ab) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        data-testid="price-input"
                        value={formData.base_price_numeric || ''}
                        onChange={(e) => {
                          const numValue = e.target.value ? parseInt(e.target.value) : null;
                          setFormData({
                            ...formData,
                            base_price_numeric: numValue,
                            base_price_from: numValue ? `ab ${numValue} €` : '',
                            price_range: numValue ? `ab ${numValue} €` : ''
                          });
                        }}
                        className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                        placeholder="z.B. 500"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Gib deinen Startpreis an, damit Kunden eine Orientierung haben</p>
                  </div>
                </div>
              </div>
            )}
            
            {step === 4 && (
              <div data-testid="onboarding-step-4">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Portfolio & Links</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Portfolio-Bilder (3-10 Bilder empfohlen)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#2B2142] transition-colors cursor-pointer">
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/heic,image/heif,image/avif"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                        data-testid="image-upload-input"
                        disabled={uploadingImages}
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <Upload className={`w-12 h-12 mx-auto mb-4 ${uploadingImages ? 'text-gray-300' : 'text-gray-400'}`} />
                        <p className={`font-semibold ${uploadingImages ? 'text-gray-400' : 'text-gray-600'}`}>
                          {uploadingImages ? 'Bilder werden hochgeladen...' : 'Bilder hochladen'}
                        </p>
                        <p className="text-sm text-gray-400 mt-2">JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC, AVIF bis 10MB</p>
                      </label>
                    </div>
                    
                    {profile?.images && profile.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        {profile.images.map((img, index) => {
                          const imageUrl = getImageUrl(img);
                          return (
                            <div key={img} className="relative group">
                              <img
                                src={imageUrl}
                                alt={`Portfolio ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg"
                                onError={(e) => {
                                  console.error('Image load error:', img);
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EBild%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              <button
                                onClick={() => handleDeleteImage(img)}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-image-${index}`}
                                type="button"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {profile?.images && profile.images.length > 0 && (
                      <p className="text-sm text-gray-600 mt-2">
                        {profile.images.length} Bild(er) hochgeladen
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Instagram
                    </label>
                    <input
                      type="url"
                      data-testid="instagram-input"
                      value={formData.instagram_link}
                      onChange={(e) => setFormData({ ...formData, instagram_link: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="https://instagram.com/deinprofil"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      data-testid="website-input"
                      value={formData.website_link}
                      onChange={(e) => setFormData({ ...formData, website_link: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="https://www.deine-website.de"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-8 py-3 border-2 border-gray-200 rounded-xl font-semibold hover:border-gray-300 transition-colors"
                  disabled={loading}
                  data-testid="back-button"
                >
                  Zurück
                </button>
              )}
              
              <button
                onClick={handleNext}
                disabled={loading || uploadingImages}
                className="ml-auto px-8 py-3 bg-[#2B2142] text-white rounded-xl font-semibold hover:bg-[#1557c0] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                data-testid="next-button"
              >
                {loading ? 'Wird gespeichert...' : step === 4 ? 'Fertig' : 'Weiter'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderOnboarding;
